import { useMemo } from 'react';
import { I18n } from '@aws-amplify/core';
import { useGetResolution886ReviewResults } from '@/hooks/v2/resolution-886';
import { useResolution886Report } from '@/context/Resolution886Report';
import { getReviewId } from '../../../_hooks/getReviewId';

const PREVENTIVE_MEASURE_QUESTIONS = {
	risk_information: {
		key: 'risk_information',
		name: I18n.get('Risk Information'),
		description: I18n.get(
			'The worker(s), supervisor(s), engineer(s), and management related to the job have been informed about the risks associated with the task of developing MSD'
		),
		sequence: 1
	},
	symptom_training: {
		key: 'symptom_training',
		name: I18n.get('Symptom Training'),
		description: I18n.get(
			'The worker(s) and supervisor(s) related to the job have been trained on identifying symptoms related to the development of MSD'
		),
		sequence: 2
	},
	symptom_identification: {
		key: 'symptom_identification',
		name: I18n.get('Symptom Identification'),
		description: I18n.get(
			'The worker(s) and supervisor(s) related to the workstation have been trained on the measures and/or procedures to prevent the development of MSD'
		),
		sequence: 3
	}
};
interface PreventiveMeasures {
	id: string;
	value: string | null;
	description: string | null;
	created_at: string;
	preventive_measure: {
		id: string;
		key: string;
		name: string;
		description: string;
		sequence: number;
	};
	question: string;
	options: {
		yes: string;
		no: string;
	};
}

export const useGetPreventiveMeasures = (report: any) => {
	const { selectedReviewId } = useResolution886Report();

	const reviewId = getReviewId(selectedReviewId, report?.reviews);

	const {
		data: reviewResults,
		isLoading,
		error
	} = useGetResolution886ReviewResults({
		report_id: report?.id,
		company_id: report?.company?.id,
		organization_id: report?.organization?.id,
		review_id: reviewId
	});

	const preventiveMeasures: PreventiveMeasures[] = useMemo(() => {
		if (!reviewResults?.preventive_measure_results) {
			return [];
		}

		return reviewResults.preventive_measure_results.map((item: any) => {
			const questionData =
				PREVENTIVE_MEASURE_QUESTIONS[item.preventive_measure.key as keyof typeof PREVENTIVE_MEASURE_QUESTIONS];

			return {
				id: item.preventive_measure.key,
				value: item.value,
				description: item.description,
				created_at: item.created_at,
				preventive_measure: {
					id: item.preventive_measure.key,
					key: item.preventive_measure.key,
					name: questionData?.name || item.preventive_measure.key,
					description: questionData?.description || `Question for ${item.preventive_measure.key}`,
					sequence: questionData?.sequence || 0
				},
				question: questionData?.description || `Question for ${item.preventive_measure.key}`,
				options: {
					yes: I18n.get('Yes'),
					no: I18n.get('No')
				}
			};
		});
	}, [reviewResults, selectedReviewId]);

	return {
		preventiveMeasures,
		isLoading,
		error
	};
};
